import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NkChanges, flatten1, isDeepEqual, isNumberArray } from '@nk/utils-lib';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import View from 'ol/View';
import { DefaultsOptions, defaults as defaultControls } from 'ol/control/defaults';
import { Coordinate } from 'ol/coordinate';
import { GeoJSON } from 'ol/format';
import { Circle, Point, Polygon } from 'ol/geom';
import Geometry from 'ol/geom/Geometry';
import Draw from 'ol/interaction/Draw';
import { Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import { OSM, Vector as VectorSource } from 'ol/source';
import {
  DEFAULT_GEO_MAP_CONFIG,
  DEFAULT_PROJECTION,
  EPSG_3857_PROJECTION,
  FIT_OPTIONS,
  FormattedArea,
  GeoGeometryGeoJson,
  GeoGeometryType,
  GeoMapConfig,
  LOCATION_MAP_BUTTON_CONTROL_CIRCLE,
  LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME,
  LOCATION_MAP_BUTTON_CONTROL_CLEAR,
  LOCATION_MAP_BUTTON_CONTROL_POLYGON,
  LOCATION_MAP_BUTTON_CONTROL_ZOOM,
  LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME,
  LOCATION_MAP_STYLE,
  LocationMapCustomButtonControl,
  LocationMapCustomControls,
  MapMarker,
  clearButtonClasses,
  formatArea,
  getCircleGeoJson,
  getCoordinatesCenter,
  getMapMarker,
  isCoordinatesArray,
  isGeoGeometryCircleGeoJson,
  isGeoGeometryPolygonGeoJson,
  setButtonVisibility,
  toggleButtonClass,
  transformLonLat,
  transformRadius,
} from './geo-map.utils';

type GeoMapCustomControlKey = keyof Omit<GeoMapConfig['controls'], 'zoom'>;

@Component({
  selector: 'nk-geo-map',
  imports: [CommonModule, TranslateModule],
  templateUrl: './geo-map.component.html',
  styleUrls: ['./geo-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GeoMapComponent implements OnInit, OnChanges {
  @Input() public andClass = '';
  @Input() public mapConfig: GeoMapConfig = { ...DEFAULT_GEO_MAP_CONFIG };
  @Input() public mapMarkers: MapMarker[] | undefined;
  @Input() public geoJson: GeoGeometryGeoJson[] | undefined;
  @Output() public addMapItem = new EventEmitter<{
    type: GeoGeometryType;
    geoJson: GeoGeometryGeoJson;
  }>();
  @Output() public areaSelectionCleared = new EventEmitter<boolean>();
  @Output() public areaCalculated = new EventEmitter<FormattedArea | null>();
  @Output() public markersCleared = new EventEmitter<boolean>();
  @Output() public mapRendered = new EventEmitter<Map | null>();
  public currentFeature: Feature | null = null;
  private _mapConfigPrevious = { ...this.mapConfig };
  private _raster!: TileLayer<OSM>;
  private _areaSelectionSource!: VectorSource<Geometry>;
  private _areaSelectionVector!: VectorLayer<VectorSource<Geometry>>;
  private _markerSource!: VectorSource<Geometry>;
  private _markerVector!: VectorLayer<VectorSource<Geometry>>;
  private _map!: Map;
  private _draw: Draw | undefined;

  private _controlsDict: Record<GeoMapCustomControlKey, LocationMapCustomButtonControl> = {
    addCircle: {
      id: LOCATION_MAP_BUTTON_CONTROL_CIRCLE,
      className: LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME,
      iconClassName: 'fa fa-dot-circle-o',
      title: this._translateService.instant(extractTranslation('geo_map.controls.circle')) as string,
      text: this._translateService.instant(extractTranslation('geo_map.controls.circle')) as string,
      clickFn: (): void => {
        const toggled = toggleButtonClass(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_CIRCLE);
        if (toggled) {
          this._addInteraction('Circle');
        } else {
          this.removeInteraction(true);
        }
      },
    },
    addPolygon: {
      id: LOCATION_MAP_BUTTON_CONTROL_POLYGON,
      className: LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME,
      iconClassName: 'fa fa-star-o',
      title: this._translateService.instant(extractTranslation('geo_map.controls.polygon')) as string,
      text: this._translateService.instant(extractTranslation('geo_map.controls.polygon')) as string,
      clickFn: (): void => {
        const toggled = toggleButtonClass(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_POLYGON);
        if (toggled) {
          this._addInteraction('Polygon');
        } else {
          this.removeInteraction(true);
        }
      },
    },
    clearMap: {
      id: LOCATION_MAP_BUTTON_CONTROL_CLEAR,
      className: `${LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME} hidden`,
      iconClassName: 'fa fa-trash',
      title: this._translateService.instant(extractTranslation('geo_map.controls.remove')) as string,
      text: this._translateService.instant(extractTranslation('geo_map.controls.remove')) as string,
      clickFn: (): void => {
        clearButtonClasses(this.mapConfig.id);
        this.removeInteraction(true, true);
        this.areaSelectionCleared.emit();
      },
    },
    setFocus: {
      id: LOCATION_MAP_BUTTON_CONTROL_ZOOM,
      className: `${LOCATION_MAP_BUTTON_CONTROL_CLASS_NAME} hidden`,
      iconClassName: 'fa fa-search-plus',
      title: this._translateService.instant(extractTranslation('geo_map.controls.zoom_to_feature')) as string,
      text: this._translateService.instant(extractTranslation('geo_map.controls.zoom_to_feature')) as string,
      clickFn: (): void => {
        const sourceFeatures = this._getFeaturesFromSource(this._areaSelectionSource);
        if (sourceFeatures?.length) {
          this.currentFeature = sourceFeatures[0];
          this._zoomToCurrentFeature(this.currentFeature);
        }
      },
    },
  };

  constructor(private _translateService: TranslateService) {}

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (this._draw && event.key === 'Escape') {
      this._draw.removeLastPoint();
    }
  }

  public ngOnInit(): void {
    setTimeout(() => {
      this._createMap();
      this._renderFeatures(this.mapMarkers, this.geoJson);
    }, 100);
  }

  public ngOnChanges(changes: NkChanges<GeoMapComponent>): void {
    const mapConfig = changes.mapConfig?.currentValue;
    if (
      this._map &&
      mapConfig &&
      !changes.mapConfig?.isFirstChange() &&
      !isDeepEqual(mapConfig, this._mapConfigPrevious)
    ) {
      // if we get a map config update it is just simplier to recreate the map than to add/remove buttons, layers, etc.
      // we skip the first change because ngOnChanges fires before ngOnInit, so we will get _createMap double run otherwise
      // _mapConfigPrevious is just for config comparison. If the map get the same config from an async pipe, we do not need to recreate the map
      this._mapConfigPrevious = { ...mapConfig };
      this._map.dispose();
      this._createMap();
    }
    const mapMarkers = changes.mapMarkers?.currentValue;
    const geoJson = changes.geoJson?.currentValue;
    this._renderFeatures(mapMarkers, geoJson);
  }

  public parseGeoJson(geoJsons: GeoGeometryGeoJson[]): void {
    let features: Feature<Geometry>[] = [];
    if (geoJsons?.length) {
      for (const geoJson of geoJsons) {
        if (isGeoGeometryPolygonGeoJson(geoJson)) {
          features = features.concat(new GeoJSON().readFeatures(geoJson, { featureProjection: EPSG_3857_PROJECTION }));
        }
        if (isGeoGeometryCircleGeoJson(geoJson)) {
          const [feature] = new GeoJSON().readFeatures(geoJson, { featureProjection: EPSG_3857_PROJECTION });
          const center = (feature.getGeometry() as Circle).getFlatCoordinates();
          const radius = transformRadius(geoJson.properties.radius, center, this._map);
          const circleFeature = new Feature(new Circle(center, radius));
          const circleCenterFeature = new Feature(new Point(center));
          features = features.concat([circleFeature, circleCenterFeature]);
        }
      }
    }
    if (features?.length) {
      this._areaSelectionSource.addFeatures(features);
      const sourceFeatures = this._getFeaturesFromSource(this._areaSelectionSource);
      if (sourceFeatures?.length) {
        this.currentFeature = sourceFeatures[0];
        setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_ZOOM, false);
        setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_CLEAR, false);
        this._calculateArea(this.currentFeature);
      }
    }
  }

  private _calculateArea(feature: Feature<Geometry> | null): void {
    if (feature) {
      const area = formatArea(feature.getGeometry() as Polygon | Circle);
      this.areaCalculated.emit(area);
    }
  }

  private _getFeaturesFromSource(source: VectorSource<Geometry>): Feature<Geometry>[] | undefined {
    return (
      source &&
      source.getFeatures().filter((i) => {
        const geometry = i.getGeometry();
        if (geometry) {
          const type = geometry.getType();
          return ['Circle', 'Polygon'].includes(type);
        }
        return undefined;
      })
    );
  }

  private _zoomToCurrentFeature(feature: Feature): void {
    if (feature) {
      const polygon = feature.getGeometry() as Polygon;
      this._map.getView().fit(polygon, FIT_OPTIONS);
    }
  }

  public removeInteraction(clearAreaSelection = false, clearMarkers = false): void {
    if (this._draw) {
      this._map.removeInteraction(this._draw);
    }
    if (clearAreaSelection) {
      this.currentFeature = null;
      this._areaSelectionSource.clear();
      setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_ZOOM, true);
      setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_CLEAR, true);
      this.areaCalculated.emit(null);
    }
    if (clearMarkers) {
      this.currentFeature = null;
      this._markerSource.clear();
      this.markersCleared.emit();
    }
  }

  private _addInteraction(type: GeoGeometryType): void {
    this.removeInteraction(true, false);

    this._draw = new Draw({
      source: this._areaSelectionSource,
      type,
    });
    this._map.addInteraction(this._draw);

    this._draw.on('drawstart', (e) => {
      this._areaSelectionSource.clear();
      if (type === 'Circle' && e.feature) {
        // the center of the circle
        const centerCoordinates = (e.feature.getGeometry() as Circle).getCenter();
        const circleCenterFeature = new Feature(new Point(centerCoordinates));
        this._areaSelectionSource.addFeatures([circleCenterFeature]);
      }
    });

    this._draw.on('drawend', (e) => {
      this.currentFeature = e.feature;
      let geoJson: GeoGeometryGeoJson;
      if (type === 'Polygon') {
        const geometry = (this.currentFeature.getGeometry() as Polygon)
          .clone()
          .transform(this._map.getView().getProjection(), DEFAULT_PROJECTION);
        const coordinates = (geometry as Polygon).getCoordinates();
        geoJson = {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates,
          },
        };
      } else {
        geoJson = getCircleGeoJson(this.currentFeature.getGeometry() as Circle, this._map);
      }
      this.addMapItem.emit({ type, geoJson });
      this.removeInteraction();
      clearButtonClasses(this.mapConfig.id);
      setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_ZOOM, false);
      setButtonVisibility(this.mapConfig.id, LOCATION_MAP_BUTTON_CONTROL_CLEAR, false);
      const area = formatArea(this.currentFeature.getGeometry() as Polygon | Circle);
      this.areaCalculated.emit(area);
    });
  }

  private _addMarker(config: MapMarker): void {
    const markerFeature = getMapMarker(config);
    this._markerSource.addFeatures([markerFeature]);
  }

  private _createCustomButtons(): LocationMapCustomButtonControl[] {
    const controls: LocationMapCustomButtonControl[] = [];
    for (const key in this.mapConfig.controls) {
      const controlKey = key as GeoMapCustomControlKey;
      if (this.mapConfig.controls[controlKey] && this._controlsDict[controlKey]) {
        controls.push(this._controlsDict[controlKey]);
      }
    }
    return controls;
  }

  private _createMap(): void {
    this._raster = new TileLayer({
      source: new OSM(),
    });

    // TODO: create separated sources for geometry and for markers
    this._areaSelectionSource = new VectorSource({
      wrapX: false,
    });

    this._areaSelectionVector = new VectorLayer({
      source: this._areaSelectionSource,
      style: LOCATION_MAP_STYLE,
    });

    this._markerSource = new VectorSource({
      wrapX: false,
    });

    this._markerVector = new VectorLayer({
      source: this._markerSource,
      style: LOCATION_MAP_STYLE,
    });

    const target = this.mapConfig.id;
    const options: DefaultsOptions = { attribution: false, zoom: this.mapConfig.controls.zoom };

    let center: Coordinate | null;
    const startCoordinates = this.mapConfig.startingPoint;
    if (isCoordinatesArray(startCoordinates)) {
      center = getCoordinatesCenter(startCoordinates);
    } else {
      center = startCoordinates;
    }

    this._map = new Map({
      controls: defaultControls(options).extend([
        new LocationMapCustomControls({
          className: LOCATION_MAP_CUSTOM_CONTROLS_CLASS_NAME,
          buttons: this._createCustomButtons(),
          target: undefined,
        }),
      ]),
      layers: [this._raster, this._areaSelectionVector, this._markerVector],
      target,
      view: new View({
        center: center ?? undefined,
        zoom: this.mapConfig.zoom,
      }),
    });
    if (isCoordinatesArray(startCoordinates)) {
      const polygon = new Polygon([startCoordinates]);
      this._map.getView().fit(polygon, FIT_OPTIONS);
    }
    this._map.on('rendercomplete', () => {
      this.mapRendered.next(this._map);
    });
  }

  private _renderFeatures(mapMarkers: MapMarker[] | undefined, geoJson: GeoGeometryGeoJson[] | undefined): void {
    if (this._map && this._areaSelectionSource) {
      if (Array.isArray(mapMarkers)) {
        this.removeInteraction(false, true);
        for (const mapMarker of mapMarkers) {
          this._addMarker({
            lat: mapMarker.lat,
            lon: mapMarker.lon,
            text: mapMarker.text,
          });
        }
      }
      if (Array.isArray(geoJson)) {
        this.removeInteraction(true, false);
        this.parseGeoJson(geoJson);
      }

      this._centerView(mapMarkers, geoJson);
    }
  }

  private _centerView(mapMarkers: MapMarker[] | undefined, geoJson: GeoGeometryGeoJson[] | undefined): void {
    // we need to check if there any coordinates (map markers or some figures)
    // if there some, we zoom to fit them all
    let mapMarkerCoordinates: Coordinate[] = [];
    let areaCoordinates: Coordinate[] = [];
    if (Array.isArray(mapMarkers)) {
      mapMarkerCoordinates = mapMarkers.map((marker) => {
        return [marker.lat, marker.lon];
      });
    }
    if (Array.isArray(geoJson)) {
      areaCoordinates = geoJson
        .filter((i) => !!i)
        .reduce((res: number[][], i) => {
          const coordinates = i.geometry.coordinates;
          if (isNumberArray(coordinates)) {
            return res.concat([coordinates]);
          }
          if (isCoordinatesArray(coordinates)) {
            return res.concat(flatten1(coordinates));
          }
          return res;
        }, [])
        .map(([lat, lon]) => {
          return transformLonLat(lat, lon);
        });
    }
    if (areaCoordinates.length) {
      const ext = this._areaSelectionSource.getExtent();
      if (isNumberArray(ext)) {
        this._map.getView().fit(ext, FIT_OPTIONS);
      }
    } else if (mapMarkerCoordinates.length) {
      const ext = this._markerSource.getExtent();
      if (isNumberArray(ext)) {
        this._map.getView().fit(ext, FIT_OPTIONS);
      }
    }
  }
}
